/**
 * @fileoverview Lesson self-assessment drawer of the student tracker, in the
 * My tasks pattern (StudentTaskDrawerShell + AssessmentDrawerBody, Mantine
 * only): no submit button — every score is autosaved as it is picked
 * (serialized per id) and the "My notes" journal is debounced. `identifierId`
 * is the lesson (session meeting) id, as in `GET /api/students/my_assessments/:id`.
 * Mirrors StudentTasks/finalAssessmentDrawer, which is the same flow for the final.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Center, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { notifications } from 'context'
import { AssessmentDrawerBody, AssessmentDrawerSkeleton, StudentTaskDrawerShell } from 'pages/StudentTasks/components'
import { dataSource } from 'pages/StudentTasks/dataSource'
import { useTaskDetail } from 'pages/StudentTasks/hooks/useTaskDetail'
import { initialScores, lessonLabel } from 'pages/StudentTasks/utils'
import type { ScoreInput, SelfAssessmentDetail } from 'pages/StudentTasks/types'

const ERROR_FALLBACK_HEIGHT = 160
/** Debounce for the "My notes" journal autosave while the student types. */
const JOURNAL_AUTOSAVE_DEBOUNCE_MS = 700

interface StudentLessonSelfAssessmentDrawerProps {
  opened: boolean
  /** Lesson (session meeting) id. */
  lessonId: string
  onClose: () => void
  /** Fires after each persisted change so the tracker can refresh its cells. */
  onSaved?: () => void
}

export function StudentLessonSelfAssessmentDrawer({
  opened,
  lessonId,
  onClose,
  onSaved,
}: StudentLessonSelfAssessmentDrawerProps) {
  const { t } = useTranslation()
  const { loading, errored, data } = useTaskDetail<SelfAssessmentDetail>(
    dataSource.fetchSelfAssessment,
    lessonId,
    opened,
  )

  const [scores, setScores] = useState<Readonly<Record<string, number>>>({})
  // Mirrors `scores` synchronously so two quick changes never merge from a stale snapshot.
  const scoresRef = useRef<Readonly<Record<string, number>>>({})
  // Serializes autosave writes per score id so quick successive clicks never race.
  const autosaveChains = useRef<Record<string, Promise<unknown>>>({})

  const [journal, setJournal] = useState('')
  const journalRef = useRef('')
  const journalDirty = useRef(false)
  const journalSaveTimeout = useRef<ReturnType<typeof setTimeout>>()
  const onSavedRef = useRef(onSaved)
  onSavedRef.current = onSaved

  const allItems = useMemo(() => (data ? [...data.skills, ...data.contents] : []), [data])

  // Seed the form whenever a (new) lesson's detail arrives.
  useEffect(() => {
    if (!data) return
    const seeded = initialScores(allItems)
    scoresRef.current = seeded
    setScores(seeded)
    autosaveChains.current = {}
    journalRef.current = data.journal
    journalDirty.current = false
    setJournal(data.journal)
  }, [data, allItems])

  const autosave = async (changed: readonly ScoreInput[], text: string): Promise<boolean> => {
    if (!data?.assessmentId) return false
    try {
      await dataSource.submitSelfAssessment(data, changed, text)
      return true
    } catch (error) {
      console.error('Error autosaving self-assessment:', error)
      return false
    }
  }

  // Persists the journal only: scores ride their own per-id autosave chains.
  const persistJournal = () => {
    if (!journalDirty.current) return
    journalDirty.current = false
    void autosave([], journalRef.current).then(ok => ok && onSavedRef.current?.())
  }

  const handleJournalChange = (text: string) => {
    journalRef.current = text
    journalDirty.current = true
    setJournal(text)
    if (journalSaveTimeout.current) clearTimeout(journalSaveTimeout.current)
    journalSaveTimeout.current = setTimeout(() => {
      journalSaveTimeout.current = undefined
      persistJournal()
    }, JOURNAL_AUTOSAVE_DEBOUNCE_MS)
  }

  // Flush a pending journal save when the drawer closes.
  useEffect(() => {
    if (opened) return
    if (journalSaveTimeout.current) {
      clearTimeout(journalSaveTimeout.current)
      journalSaveTimeout.current = undefined
    }
    persistJournal()
    // Only the open/close transition matters here; the journal state is read through refs.
  }, [opened])

  const handleScoreChange = (id: string, score: number) => {
    const next = { ...scoresRef.current, [id]: score }
    scoresRef.current = next
    setScores(next)
    // The journal is sent along so a note typed before the first score is never dropped.
    const previous = autosaveChains.current[id] ?? Promise.resolve()
    autosaveChains.current[id] = previous
      .then(() => autosave([{ id, score }], journalRef.current))
      .then(ok => {
        if (!ok) return
        if (score > 0) {
          notifications.show({ message: t('arc_teacher_assessment_drawer.toast.saved'), color: 'green' })
        }
        onSavedRef.current?.()
      })
      .catch(() => undefined)
  }

  return (
    <StudentTaskDrawerShell opened={opened} title={t('student_tasks.assessment_drawer.self_title')} onClose={onClose}>
      {loading ? (
        <AssessmentDrawerSkeleton />
      ) : errored || !data ? (
        <Center style={{ height: ERROR_FALLBACK_HEIGHT }}>
          <Text color='dimmed' size='sm'>
            {t('student_tasks.assessment_drawer.error_loading')}
          </Text>
        </Center>
      ) : (
        <AssessmentDrawerBody
          key={data.identifierId}
          projectName={data.projectName}
          lesson={lessonLabel(data.date, data.title)}
          skills={data.skills}
          contents={data.contents}
          scores={scores}
          journal={journal}
          onScoreChange={handleScoreChange}
          onJournalChange={handleJournalChange}
        />
      )}
    </StudentTaskDrawerShell>
  )
}
